import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import i18n, { resolveLocale } from './index'

interface Preferences {
  homePage: string | null
  icalFeedToken: string | null
  uiLocale: string | null
  /** The studio's team language (StudioSettings.uiLocale) — null when never stated. */
  studioUiLocale: string | null
}

/**
 * Syncs i18next's active language with what the backend knows about the
 * logged-in user. Mounted once near the top of the authenticated tree —
 * fetches /me/preferences and applies the first answer that exists.
 *
 * ## The chain (D2)
 *
 *   1. `uiLocale` — this member's own explicit preference.
 *   2. `studioUiLocale` — the language the STUDIO'S TEAM works in. Not the
 *      language it writes to its clients in: `StudioSettings.locale` is a
 *      separate setting with a separate control, and a studio may well work in
 *      English while corresponding in German.
 *   3. the browser, via LanguageDetector at i18next init (navigator.language,
 *      or the localStorage cache of an earlier decision).
 *   4. `en-US`, i18next's `fallbackLng`.
 *
 * Steps 3 and 4 have already happened by the time this hook runs, which is why
 * it only has to decide between 1 and 2 and otherwise leave the detector's
 * choice alone.
 *
 * Step 1 is seeded at invite acceptance (IN-39): the backend writes the studio's
 * team language onto the membership when it is bound, so anybody who joined after
 * that change answers at step 1 and never reaches step 2. Step 2 stays for the
 * memberships that predate it, and `AcceptInvitePage` applies the same value on
 * the acceptance screen itself — where there is no membership yet to read.
 *
 * **`StudioLocaleResolver.forMemberUI` states the identical chain server-side,
 * and the two name each other on purpose.** Backend-rendered strings (attention
 * items, the digest email, bell rows) land beside strings this file's language
 * choice translated; when one side gains a step the other has not heard of, the
 * page shows two languages at once. That has now happened twice — F5 (the
 * backend fell back to the client-facing locale, which the frontend has never
 * consulted) and F40 — so a change here is a change there, in the same commit.
 *
 * Why not just detect from the browser on every load: the detector's
 * localStorage cache wins over per-user preference in the normal i18next
 * startup flow. But the user might be on a shared machine where someone else's
 * preference is cached. This hook forces the server-stored answer to win once
 * we actually know who they are.
 *
 * Errors from the preference fetch are swallowed (language isn't critical
 * enough to gate the UI on).
 *
 * **The two chains agreeing is not sufficient on its own (IN-37).** Even when both
 * sides reach the same answer, the strings the server already rendered are sitting
 * in a TanStack cache that a client-side `changeLanguage` does not touch — so the
 * chrome flips and the sentences inside it do not, until something refetches them.
 * `useServerRenderedTextRefresh` is that something; it listens for the
 * `languageChanged` this hook fires as well as the one Settings fires.
 */
export function useUiLocaleSync() {
  const { data } = useQuery<Preferences>({
    queryKey: queryKeys.settings.preferences,
    queryFn: async () => {
      const res = await apiClient.get('/team-members/me/preferences')
      return res.data
    },
    // Preferences rarely change; avoid refetching on every window focus.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  useEffect(() => {
    // Step 1 then step 2. `??` rather than `||` is not enough here — the API sends
    // null, but an empty string would also mean "not stated" — so test for a
    // non-empty value explicitly.
    const stated = data?.uiLocale || data?.studioUiLocale
    // Neither stated: the detector already picked (browser, then en-US) and its
    // choice is the correct answer, not a gap to fill.
    if (!stated) return
    const target = resolveLocale(stated)
    if (i18n.language !== target) {
      void i18n.changeLanguage(target)
    }
  }, [data?.uiLocale, data?.studioUiLocale])
}
